import React from 'react';
import {
  EntityDependsOnComponentsCard,
  EntityHasSubcomponentsCard,
  EntityLayout,
  EntitySwitch,
  isComponentType,
} from '@backstage/plugin-catalog';
import { Grid } from '@material-ui/core';
import {
  EntityChangelogCard,
  isChangelogAnnotationConfigurationOk,
} from '@rsc-labs/backstage-changelog-plugin';
import {
  EntityConsumedApisCard,
  EntityProvidedApisCard,
} from '@backstage/plugin-api-docs';
import {
  isGitlabAvailable,
  EntityGitlabLanguageCard,
  EntityGitlabMergeRequestsTable,
  EntityGitlabMergeRequestStatsCard,
  EntityGitlabPeopleCard,
  EntityGitlabPipelinesTable,
  EntityGitlabReadmeCard,
  EntityGitlabReleasesCard,
} from '@immobiliarelabs/backstage-plugin-gitlab';
import { OverviewContent } from './OverviewContent';
import { CiCdContent } from './CiCdContent';
import { TechdocsContent } from './TechdocsContent';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { isSonarQubeAvailable } from '@backstage-community/plugin-sonarqube-react';
import { ContainerContextDiagramCard } from '../../cards/ContainerContextDiagramCard';
import { HasResourcesCard } from '../../cards/HasResourcesCard';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { isAdrAvailable } from '@backstage-community/plugin-adr';
import { AdrContent } from '../AdrContent';

function hasAPIs(entity: Entity): boolean {
  return hasConsumedAPIs(entity) || hasProvidedAPIs(entity);
}

function hasConsumedAPIs(entity: Entity): boolean {
  return !!entity.relations?.find(
    it =>
      parseEntityRef(it.targetRef).kind === 'api' && it.type === 'consumesApi',
  );
}

function hasProvidedAPIs(entity: Entity): boolean {
  return !!entity.relations?.find(
    it =>
      parseEntityRef(it.targetRef).kind === 'api' && it.type === 'providesApi',
  );
}

function hasComponents(entity: Entity): boolean {
  return hasSubComponents(entity) || hasDependendComponents(entity);
}

function hasSubComponents(entity: Entity): boolean {
  return !!entity.relations?.find(
    it =>
      parseEntityRef(it.targetRef).kind === 'component' &&
      it.type === 'hasPart',
  );
}

function hasDependendComponents(entity: Entity): boolean {
  return !!entity.relations?.find(
    it =>
      parseEntityRef(it.targetRef).kind === 'component' &&
      it.type === 'dependsOn',
  );
}

function hasResources(entity: Entity): boolean {
  return !!entity.relations?.find(
    it => parseEntityRef(it.targetRef).kind === 'resource',
  );
}

function hasSystem(entity: Entity): boolean {
  return !!entity.spec?.system;
}

export const ComponentPage = () => (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <OverviewContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD" if={isGitlabAvailable}>
      <CiCdContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/api"
      title="API"
      if={isComponentType('service') && hasAPIs}
    >
      <Grid container spacing={3} alignItems="stretch">
        <EntitySwitch>
          <EntitySwitch.Case if={hasProvidedAPIs}>
            <Grid item xs={12}>
              <EntityProvidedApisCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasConsumedAPIs}>
            <Grid item xs={12}>
              <EntityConsumedApisCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/components"
      title="Components"
      if={hasComponents}
    >
      <Grid container spacing={3} alignItems="stretch">
        <EntitySwitch>
          <EntitySwitch.Case if={hasSubComponents}>
            <Grid item xs={12}>
              <EntityHasSubcomponentsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasDependendComponents}>
            <Grid item xs={12}>
              <EntityDependsOnComponentsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/resources" title="Resources" if={hasResources}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <HasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/code" title="Code" if={isGitlabAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <EntityGitlabReadmeCard />
        </Grid>
        <Grid item sm={12} md={3} lg={3}>
          <EntityGitlabPeopleCard />
        </Grid>
        <Grid item sm={12} md={3} lg={3}>
          <EntityGitlabLanguageCard />
        </Grid>
        <Grid item sm={12} md={3} lg={3}>
          <EntityGitlabMergeRequestStatsCard />
        </Grid>
        <Grid item sm={12} md={3} lg={3}>
          <EntityGitlabReleasesCard />
        </Grid>
        <Grid item md={12}>
          <EntityGitlabPipelinesTable />
        </Grid>
        <Grid item md={12}>
          <EntityGitlabMergeRequestsTable />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/quality"
      title="Quality"
      if={isSonarQubeAvailable}
    >
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntitySonarQubeCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isTechDocsAvailable}>
      <TechdocsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/adrs" title="ADRs" if={isAdrAvailable}>
      <AdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/changelog"
      title="Changelog"
      if={isChangelogAnnotationConfigurationOk}
    >
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityChangelogCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/context" title="Context-Diagram" if={hasSystem}>
      <ContainerContextDiagramCard />
    </EntityLayout.Route>
  </EntityLayout>
);
